// What an upload sends to the database: secrets stripped, only new or changed rows.
import { defaultRoleOf, ucoQty } from './appointment';
import { clean } from './clean';
import { parseCoords } from './geo';
import type { Appointment, AppointmentUpsert, SnapshotStats } from './types';

// Never sent to the database: account secrets the export happens to carry, and bulky texts.
const DROP_KEYS = new Set([
  'passwordHash', 'resetToken', 'resetTokenExpires', 'verificationToken', 'verificationExpires',
  'googleId', 'facebookId', 'appleId', 'description', 'imgUrl',
]);

/** A deep copy without the secret and bulky fields, at any depth. */
export function strip(a: Appointment): Appointment {
  return JSON.parse(JSON.stringify(a, (k, v) => (DROP_KEYS.has(k) ? undefined : v))) as Appointment;
}

/** JSON with sorted keys, so two copies of the same record compare equal. */
export function canon(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v === undefined ? null : v);
  if (Array.isArray(v)) return '[' + v.map(canon).join(',') + ']';
  const o = v as Record<string, unknown>;
  return (
    '{' +
    Object.keys(o)
      .sort()
      .filter((k) => o[k] !== undefined)
      .map((k) => JSON.stringify(k) + ':' + canon(o[k]))
      .join(',') +
    '}'
  );
}

/** Figures stored with every upload, for the backlog trend in History. */
export function snapshotStats(list: Appointment[]): SnapshotStats {
  const c = clean(list);
  const s: SnapshotStats = {
    total: list.length, kept: c.list.length, pending: 0, pendingL: 0, postponed: 0,
    collected: 0, collectedL: 0, cancelled: 0, pendingNoPos: 0,
  };
  c.list.forEach((a) => {
    const role = defaultRoleOf(a.status), q = ucoQty(a);
    if (role === 'pending') {
      s.pending++;
      s.pendingL += q;
      if (String(a.status).toLowerCase() === 'postponed') s.postponed++;
      if (!parseCoords(a).ok) s.pendingNoPos++;
    } else if (role === 'collected') {
      s.collected++;
      s.collectedL += q;
    } else s.cancelled++;
  });
  return s;
}

export interface IngestPlan {
  total: number;
  nNew: number;
  nChanged: number;
  same: number;
  changed: Appointment[];
  /** what the database will hold afterwards (for the snapshot figures) */
  merged: Appointment[];
}

/** Compares an export with what is already saved. */
export function planIngest(current: Appointment[], incomingRaw: Appointment[]): IngestPlan {
  const cur = new Map<string, Appointment>();
  current.forEach((a) => {
    if (a && a.id) cur.set(a.id, a);
  });
  const incoming = new Map<string, Appointment>();
  incomingRaw.forEach((a) => {
    if (a && a.id) incoming.set(a.id, strip(a));
  });
  const changed: Appointment[] = [];
  let nNew = 0, nChanged = 0;
  incoming.forEach((a, id) => {
    const old = cur.get(id);
    if (!old) {
      nNew++;
      changed.push(a);
      return;
    }
    if (canon(old) !== canon(a)) {
      nChanged++;
      changed.push(a);
    }
  });
  const merged = new Map(cur);
  changed.forEach((a) => merged.set(a.id, a));
  return { total: incoming.size, nNew, nChanged, same: incoming.size - nNew - nChanged, changed, merged: [...merged.values()] };
}

function iso(v: string | null | undefined): string | null {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d.getTime()) ? d.toISOString() : null;
}

export function toUpsertRow(a: Appointment, snapshotId: number): AppointmentUpsert {
  return {
    id: a.id,
    status: a.status || null,
    created_at: iso(a.createdAt),
    updated_at: iso(a.updatedAt),
    scheduled_date: iso(a.scheduledDate),
    simple_user_id: a.simpleUserId || (a.simpleUser && a.simpleUser.id) || null,
    location_code: a.locationCode || null,
    raw: a,
    first_snapshot: snapshotId,
    last_snapshot: snapshotId,
  };
}
